import Typography from "@mui/material/Typography";
import React, { useContext, useEffect, useState } from "react";
import { ArbeitsplanType, Terminarten, UserType } from "../utils/types";
import "../App.css";
import Timeline from "react-calendar-timeline";
import {
  TimelineHeaders,
  DateHeader,
  TodayMarker,
  CustomMarker,
  TimelineMarkers,
  CursorMarker,
} from "react-calendar-timeline/lib";
import "react-calendar-timeline/lib/Timeline.css";
import moment from "moment";
import AuthContext from "../context/AuthContext";
import { Button } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import {
  getHoursFromGesamtTermin,
  replaceEnglishMonthWithGerman,
} from "../utils/helperFunctions";
import { Spring, animated } from "react-spring";
import { PageWrapper } from "../components/common/PageWrapper";
import { SubPageTitleWithUnderline } from "../components/common/SubPageTitleWithUnderline";

const abwesenheitsLegende = [
  {
    color: "green",
    grouping: "Urlaub",
    terminArten: [
      Terminarten["Urlaub"],
      Terminarten["Überstunden"],
      Terminarten["Freizeitausgleich"],
    ],
  },
  {
    color: "lightgreen",
    grouping: "Urlaubsanfrage",
    terminArten: [
      Terminarten["Urlaubsanfrage"],
      Terminarten["Freizeitausgleich (angefragt)"],
    ],
  },
  {
    color: "yellow",
    grouping: "Krank",
    terminArten: [Terminarten["Krank"], Terminarten["Arbeitsunfall"]],
  },
  {
    color: "blue",
    grouping: "Schule/Fortbildung",
    terminArten: [
      Terminarten["Weiterbildung"],
      Terminarten["Schule"],
      Terminarten["überbetriebliche Unterweisung"],
      Terminarten["Prüfung"],
    ],
  },
  {
    color: "red",
    grouping: "Büro",
    terminArten: [Terminarten["Büro"]],
  },
  {
    color: "purple",
    grouping: "Geburtstag",
    terminArten: [Terminarten["Geburtstag"]],
  },
];

const KalenderPage = () => {
  const [abwesenheiten, setAbwesenheiten] = useState<ArbeitsplanType[]>([]);
  const [feiertage, setFeiertage] = useState<ArbeitsplanType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [visibleTimeStart, setVisibleTimeStart] = useState(
    moment().subtract(14, "days").valueOf()
  );
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(
    moment().add(14, "days").valueOf()
  );
  const [width, setWidth] = useState<number>(window.innerWidth);

  let { user } = useContext(AuthContext);
  const currentUser: UserType = user;

  useEffect(() => {
    const getArbeitsplan = async () => {
      const response = await fetch(`/api/getArbeitsplan/`, {
        cache: "no-cache",
      });
      const data = await response.json();
      const abwesenheit = data.filter((x: ArbeitsplanType) =>
        abwesenheitsLegende.some((legende) =>
          legende.terminArten.includes(x.terminart)
        )
      );
      setAbwesenheiten(abwesenheit);
      const feiertag = data.filter(
        (x: ArbeitsplanType) => x.terminart === Terminarten["Feiertag"]
      );
      setFeiertage(feiertag);
    };
    getArbeitsplan();
  }, []);

  useEffect(() => {
    const getAllUsers = async () => {
      const response = await fetch(`/api/getAllUsers/`);
      const data = await response.json();
      setUsers(data.filter((x: UserType) => x.username !== "AlexH"));
    };
    getAllUsers();
  }, []);

  const isMobile = width <= 700;

  const groups = [];
  const items = [];

  // Add all users to the groups
  for (let i = 0; i < users.length; i++) {
    if (!users[i] || users[i].is_superuser || !users[i].mitarbeiternummer) {
      continue;
    }
    groups.push({
      id: users[i].mitarbeiternummer,
      title: users[i].last_name + ", " + users[i].first_name,
    });
  }

  for (let i = 0; i < abwesenheiten.length; i++) {
    const personId = abwesenheiten[i].mitarbeiter;

    items.push({
      id: abwesenheiten.length + i,
      title: abwesenheiten[i].bemerkung,
      group: personId,
      start_time: moment(
        abwesenheiten[i].gesamttermindatumvon,
        "YYYY-MM-DD"
      ).add(
        getHoursFromGesamtTermin(abwesenheiten[i].gesamtterminvon),
        "hours"
      ),
      end_time: abwesenheiten[i].gesamttermindatumbis
        ? moment(abwesenheiten[i].gesamttermindatumbis, "YYYY-MM-DD").add(
            getHoursFromGesamtTermin(abwesenheiten[i].gesamtterminbis),
            "hours"
          )
        : moment(abwesenheiten[i].gesamttermindatumvon, "YYYY-MM-DD").add(
            getHoursFromGesamtTermin(abwesenheiten[i].gesamtterminbis),
            "hours"
          ),
      itemProps: {
        style: {
          background: abwesenheitsLegende.find((x) =>
            x.terminArten.includes(abwesenheiten[i].terminart)
          ).color,
        },
      },
      canMove: false,
    });
  }
  // Sort the groups alphabetically
  groups.sort((a, b) => a.title.localeCompare(b.title));

  // Move current logged in user to the top of the list
  if (currentUser) {
    const currentUserGroup = groups.find(
      (group) =>
        group.id.toString() === currentUser.mitarbeiternummer.toString()
    );
    if (currentUserGroup) {
      const currentUserIndex = groups.indexOf(currentUserGroup);
      groups.splice(currentUserIndex, 1);
      groups.unshift(currentUserGroup);
    }
  }

  const onPrevClick = () => {
    const zoom = visibleTimeEnd.valueOf() - visibleTimeStart.valueOf();

    setVisibleTimeStart(visibleTimeStart.valueOf() - zoom.valueOf());
    setVisibleTimeEnd(visibleTimeEnd.valueOf() - zoom.valueOf());
  };

  const onNextClick = () => {
    const zoom = visibleTimeEnd - visibleTimeStart;
    setVisibleTimeStart(visibleTimeStart + zoom);
    setVisibleTimeEnd(visibleTimeEnd + zoom);
  };

  const goToThisMonth = () => {
    setVisibleTimeStart(moment().subtract(14, "days").valueOf());
    setVisibleTimeEnd(moment().add(14, "days").valueOf());
  };

  const AnimatedTimeline = animated(
    ({
      animatedVisibleTimeStart,
      animatedVisibleTimeEnd,
      visibleTimeStart,
      visibleTimeEnd,
      ...props
    }) => (
      <Timeline
        visibleTimeStart={animatedVisibleTimeStart}
        visibleTimeEnd={animatedVisibleTimeEnd}
        {...props}
      />
    )
  );

  if (currentUser.bemerkung != "GF" && currentUser.bemerkung != "Personal") {
    return <p>Keine Berechtigung</p>;
  }

  // TODO: for some reason the TodayMarker is not working correctly
  // TODO: DatePickers are not working correctly
  return (
    <PageWrapper>
      <div className="d-flex justify-content-between mb-3">
        <div style={{ width: "100%" }}>
          <SubPageTitleWithUnderline title={"Kalender"} />
        </div>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Datum wählen"
            sx={{ zIndex: 100, marginLeft: "2em" }}
            className="datePicker"
            format="DD.MM.YYYY"
            slotProps={{ textField: { size: "small" } }}
            onChange={(date: Dayjs) => {
              setVisibleTimeStart(
                moment(date.valueOf()).subtract(14, "days").valueOf()
              );
              setVisibleTimeEnd(
                moment(date.valueOf()).add(14, "days").valueOf()
              );
            }}
          />
        </LocalizationProvider>
      </div>
      <Legende />
      <div
        className="button-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#e30713",
          height: "20px",
        }}
      >
        <Button
          variant="contained"
          onClick={onPrevClick}
          style={{ backgroundColor: "#e30713" }}
        >
          {isMobile ? "<-" : "4 Wochen zurück"}
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "#e30713" }}
          onClick={goToThisMonth}
        >
          Heute
        </Button>
        <Button
          variant="contained"
          onClick={onNextClick}
          style={{ backgroundColor: "#e30713" }}
        >
          {isMobile ? "->" : "4 Wochen vor"}
        </Button>
      </div>

      <div>
        <Spring
          to={{
            animatedVisibleTimeStart: visibleTimeStart,
            animatedVisibleTimeEnd: visibleTimeEnd,
          }}
        >
          {(value) => (
            <AnimatedTimeline
              className="calendarTimeline"
              groups={groups}
              items={items}
              // defaultTimeStart={moment().add(-7, "day")}
              // defaultTimeEnd={moment().add(21, "day")}

              // visibleTimeStart={firstDayOfMonth}
              // visibleTimeEnd={lastDayOfMonth}
              // minZoom={(365.24 * 86400 * 1000) / 12 / 4} // how much can I zoom out (bigger=see less days)
              // maxZoom={(365.24 * 86400 * 1000) / 12} // how much can I zoom in (smaller=see less days)
              // style={{ backgroundColor: "lightblue" }} // Set the background color of the timeline
              {...value}
            >
              <TimelineMarkers>
                <TodayMarker>
                  {({ styles }) => {
                    const newStyles = {
                      ...styles,
                      backgroundColor: "#e30713",
                    };
                    return <div style={newStyles} />;
                  }}
                </TodayMarker>
                <CursorMarker />
                {feiertage.map((feiertag) => (
                  <CustomMarker date={moment(feiertag.datum, "YYYY-MM-DD")}>
                    {({ styles }) => {
                      const columnWidth =
                        document.querySelector(".rct-vl.rct-day-0")
                          ?.clientWidth + 1 || 5;
                      const newStyles = {
                        ...styles,
                        backgroundColor: "green",
                        width: columnWidth,
                      };
                      return <div style={newStyles} />;
                    }}
                  </CustomMarker>
                ))}
              </TimelineMarkers>

              <TimelineHeaders
                className="sticky"
                style={{ backgroundColor: "#e30713" }}
              >
                <DateHeader
                  unit="primaryHeader"
                  height={20}
                  style={{ backgroundColor: "#e30713" }}
                  labelFormat={(
                    [startTime, endTime]: [moment.Moment, moment.Moment],
                    unit: any,
                    labelWidth: number,
                    formatOptions: any = "MMMM YYYY"
                  ) => {
                    return replaceEnglishMonthWithGerman(
                      startTime.format("MMMM YYYY")
                    );
                  }}
                />
                <DateHeader unit="week" labelFormat="[KW] W" height={20} />
                {!isMobile && <DateHeader unit="day" height={20} />}
              </TimelineHeaders>
            </AnimatedTimeline>
          )}
        </Spring>
      </div>
    </PageWrapper>
  );
};

export default KalenderPage;

const Legende = () => {
  return (
    <>
      {abwesenheitsLegende.map((legende) => (
        <div
          key={legende.grouping}
          style={{
            display: "inline-block",
            marginRight: 20,
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              backgroundColor: legende.color,
              display: "inline-block",
              marginRight: 5,
            }}
          />
          <Typography
            variant="body2"
            style={{ display: "inline-block", position: "relative", top: -4 }}
          >
            {legende.grouping}
          </Typography>
        </div>
      ))}
    </>
  );
};
